import React from 'react';
import { useLanguage, type Language } from '../../contexts/LanguageContext';
import styles from './LanguageToggle.module.css';

/**
 * AB#1660 — In-page language toggle for the Auth v2 shell.
 *
 * Sits at the top-right of the auth-form panel (signup, login, forgot-password,
 * partner-request, LoginChooser, ElevationModal). Mirrors the in-portal
 * post-auth Menu language toggle (Header → Menu) but in a compact `EN | AR`
 * pill-style segmented control rather than the full-word menu rows — the auth
 * shell is space-constrained and Booksy/Fresha precedent favors the compact
 * form here.
 *
 * Persistence is handled by `LanguageContext` (`devanza_language` localStorage
 * key) so this component is purely a renderer over the context's
 * `language` + `setLanguage` API. After successful signup the JWT `locale`
 * claim takes over via the same context — no double-write.
 *
 * RTL: in AR layout the segmented control mirrors automatically because the
 * parent uses logical layout; the visually-leading segment matches the
 * active language regardless of locale direction.
 */
const LanguageToggle: React.FC<{ className?: string }> = ({ className }) => {
    const { language, setLanguage } = useLanguage();

    const select = (lang: Language) => {
        if (lang === language) return;
        setLanguage(lang);
    };

    return (
        <div
            className={`${styles.toggle} ${className ?? ''}`}
            role="group"
            aria-label="Language"
        >
            <button
                type="button"
                className={`${styles.option} ${language === 'en' ? styles.active : ''}`}
                onClick={() => select('en')}
                aria-pressed={language === 'en'}
                aria-label="English"
            >
                EN
            </button>
            <span className={styles.separator} aria-hidden="true">·</span>
            <button
                type="button"
                className={`${styles.option} ${language === 'ar' ? styles.active : ''}`}
                onClick={() => select('ar')}
                aria-pressed={language === 'ar'}
                aria-label="العربية"
            >
                AR
            </button>
        </div>
    );
};

export default LanguageToggle;
